/**
 * Deterministic RNG + samplers for the pricing kernel.
 *
 * mulberry32 matches the thin-slice validation runner (plan §9bis.1) so kernel
 * outputs are byte-replayable from (seed, draws, input bundle) alone.
 */

export type Rng = () => number;

export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Marsaglia–Tsang gamma sampler (shape >= 0 handled via boost), deterministic. */
export function sampleGamma(rng: Rng, shape: number): number {
  if (shape <= 0) return 0;
  if (shape < 1) {
    // Boost: Gamma(a) = Gamma(a+1) * U^(1/a)
    const u = Math.max(rng(), Number.MIN_VALUE);
    return sampleGamma(rng, shape + 1) * Math.pow(u, 1 / shape);
  }
  const d = shape - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);
  for (;;) {
    let x: number;
    let v: number;
    do {
      x = sampleStandardNormal(rng);
      v = 1 + c * x;
    } while (v <= 0);
    v = v * v * v;
    const u = Math.max(rng(), Number.MIN_VALUE);
    if (Math.log(u) < 0.5 * x * x + d - d * v + d * Math.log(v)) {
      return d * v;
    }
  }
}

/** Box–Muller from the deterministic uniform stream. */
export function sampleStandardNormal(rng: Rng): number {
  const u1 = Math.max(rng(), Number.MIN_VALUE);
  const u2 = rng();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

/** Beta(a, b) via two gammas. */
export function sampleBeta(rng: Rng, a: number, b: number): number {
  const x = sampleGamma(rng, a);
  const y = sampleGamma(rng, b);
  const sum = x + y;
  return sum > 0 ? x / sum : 0;
}

/**
 * Dirichlet over K cells with Jeffreys-style +0.5 prior per cell.
 * Returns probabilities summing to 1.
 */
export function sampleDirichlet(rng: Rng, counts: number[], prior = 0.5): number[] {
  const gammas = counts.map((count) => sampleGamma(rng, count + prior));
  const total = gammas.reduce((sum, value) => sum + value, 0);
  if (total <= 0) {
    const uniform = 1 / counts.length;
    return counts.map(() => uniform);
  }
  return gammas.map((value) => value / total);
}

export function quantile(sortedValues: number[], q: number): number {
  if (sortedValues.length === 0) return NaN;
  const pos = (sortedValues.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  const next = sortedValues[base + 1];
  return next !== undefined
    ? sortedValues[base] + rest * (next - sortedValues[base])
    : sortedValues[base];
}
